import { Component, inject } from '@angular/core';
import { RouterLink } from '@angular/router';
import { SiteConfigService } from '../../../core/config/site-config.service';

@Component({
  selector: 'app-footer',
  standalone: true,
  imports: [RouterLink],
  template: `
    <footer class="bg-surface-950 text-surface-400 relative overflow-hidden">
      <!-- Brand accent line -->
      <div class="h-[2px] bg-gradient-to-r from-transparent via-brand-500/40 to-transparent"></div>

      <div class="mx-auto max-w-7xl px-6 lg:px-8">
        <!-- Main -->
        <div class="grid grid-cols-1 gap-12 pt-20 pb-14 md:grid-cols-12">
          <!-- Brand -->
          <div class="md:col-span-4">
            <div class="flex items-center gap-3 mb-5">
              <div class="w-9 h-9 rounded-lg bg-brand-600 flex items-center justify-center">
                <span class="text-white font-heading font-bold text-sm">{{ brand.logoInitials }}</span>
              </div>
              <div class="flex flex-col leading-tight">
                <span class="font-heading text-[1rem] font-bold text-white tracking-[-0.02em]">{{ brand.shortName }}</span>
                <span class="text-[0.55rem] font-semibold uppercase tracking-[0.18em] text-surface-500">{{ brand.name.replace(brand.shortName, '').trim() || brand.tagline }}</span>
              </div>
            </div>
            <p class="text-sm leading-relaxed text-surface-400 max-w-sm mb-6">
              {{ seo.defaultDescription }}
            </p>
            <div class="flex items-center gap-2.5">
              @for (s of socials; track s.label) {
                <a href="#" [attr.aria-label]="s.label"
                   class="flex items-center justify-center h-9 w-9 rounded-lg bg-white/[0.06] text-surface-400 transition-all duration-200 hover:bg-brand-600/20 hover:text-brand-400">
                  <span class="text-xs font-bold">{{ s.abbr }}</span>
                </a>
              }
            </div>
          </div>

          <!-- Quick Links -->
          <nav class="md:col-span-2" aria-label="Footer navigation">
            <h3 class="text-[0.6875rem] font-semibold uppercase tracking-[0.15em] text-surface-500 mb-5">Company</h3>
            <ul class="space-y-3">
              @for (link of quickLinks; track link.label) {
                <li><a [routerLink]="link.path" class="text-sm text-surface-400 transition-colors duration-200 hover:text-white">{{ link.label }}</a></li>
              }
            </ul>
          </nav>

          <!-- Services -->
          <div class="md:col-span-3">
            <h3 class="text-[0.6875rem] font-semibold uppercase tracking-[0.15em] text-surface-500 mb-5">Services</h3>
            <ul class="space-y-3">
              @for (service of services; track service) {
                <li><a routerLink="/services" class="text-sm text-surface-400 transition-colors duration-200 hover:text-white">{{ service }}</a></li>
              }
            </ul>
          </div>

          <!-- Contact -->
          <div class="md:col-span-3">
            <h3 class="text-[0.6875rem] font-semibold uppercase tracking-[0.15em] text-surface-500 mb-5">Get in Touch</h3>
            <ul class="space-y-3.5 text-sm">
              <li class="flex items-start gap-3">
                <div class="flex items-center justify-center w-8 h-8 rounded-lg bg-white/[0.06] shrink-0">
                  <svg aria-hidden="true" class="w-4 h-4 text-surface-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" /><path stroke-linecap="round" stroke-linejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" /></svg>
                </div>
                <span>{{ contact.address.street }}<br/>{{ contact.address.city }}, {{ contact.address.state }} {{ contact.address.zip }}</span>
              </li>
              <li class="flex items-center gap-3">
                <div class="flex items-center justify-center w-8 h-8 rounded-lg bg-white/[0.06] shrink-0">
                  <svg aria-hidden="true" class="w-4 h-4 text-surface-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" /></svg>
                </div>
                <a [href]="'tel:' + contact.phone.replace(/[^+\\d]/g, '')" class="hover:text-white transition-colors">{{ contact.phone }}</a>
              </li>
              <li class="flex items-center gap-3">
                <div class="flex items-center justify-center w-8 h-8 rounded-lg bg-white/[0.06] shrink-0">
                  <svg aria-hidden="true" class="w-4 h-4 text-surface-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" /></svg>
                </div>
                <a [href]="'mailto:' + contact.email" class="hover:text-white transition-colors">{{ contact.email }}</a>
              </li>
            </ul>
            <div class="mt-5 p-3.5 rounded-xl bg-white/[0.04] border border-white/[0.06]">
              <div class="text-[0.6875rem] font-semibold uppercase tracking-[0.12em] text-surface-500 mb-2">Office Hours</div>
              <div class="text-xs space-y-1">
                @for (h of contact.hours; track h.label) {
                  <div class="flex justify-between"><span>{{ h.label }}</span><span class="text-surface-300">{{ h.value }}</span></div>
                }
              </div>
            </div>
          </div>
        </div>

        <!-- Bottom bar -->
        <div class="border-t border-white/[0.06] py-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p class="text-xs text-surface-600">
            &copy; {{ year }} {{ brand.name }}. Licensed & Insured — FL License #CGC123456
          </p>
          <div class="flex items-center gap-5 text-xs text-surface-600">
            <a href="#" class="hover:text-surface-400 transition-colors">Privacy Policy</a>
            <a href="#" class="hover:text-surface-400 transition-colors">Terms</a>
            <a routerLink="/sitemap" class="hover:text-surface-400 transition-colors">Sitemap</a>
          </div>
        </div>
      </div>
    </footer>
  `,
})
export class FooterComponent {
  private config = inject(SiteConfigService);

  year = new Date().getFullYear();
  brand = this.config.brand;
  contact = this.config.contact;
  seo = this.config.seo;
  quickLinks = this.config.footerLinks;
  services = this.config.footerServices;
  socials = this.config.socials;
}
